import React, { useMemo, useState } from 'react';
import { Layout, Form, Input, Button, Typography, message, Select, DatePicker, Tag, Row, Col, Grid } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PhoneOutlined,
  UserOutlined,
  MailOutlined,
  ShopOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  LockOutlined,
} from '@ant-design/icons';
import api from '../api';
import './SignupAdmin.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const STEP_1_FIELDS = [
  'name',
  'phone',
  'businessName',
  'password',
  'businessEmail',
  'address',
  'state',
  'city',
  'gstNumber',
];

const label = (text) => <Text strong style={{ fontSize: 13 }}>{text}</Text>;

function SignupStepNav({ step, onStepChange }) {
  const items = [
    { key: 0, short: 'Account', full: 'Business & account' },
    { key: 1, short: 'Details', full: 'More details' },
  ];

  return (
    <div className="signup-admin-step-nav" role="tablist" aria-label="Signup steps">
      {items.map((item, index) => {
        const isCurrent = step === item.key;
        const isDone = step > item.key;
        const canGoBack = item.key < step;

        return (
          <React.Fragment key={item.key}>
            {index > 0 && <div className={`signup-admin-step-nav__divider ${isDone || isCurrent ? 'is-active' : ''}`} aria-hidden />}
            <button
              type="button"
              role="tab"
              aria-selected={isCurrent}
              aria-current={isCurrent ? 'step' : undefined}
              className={[
                'signup-admin-step-nav__item',
                isCurrent ? 'is-current' : '',
                isDone ? 'is-done' : '',
              ].filter(Boolean).join(' ')}
              disabled={!canGoBack}
              onClick={() => canGoBack && onStepChange(item.key)}
            >
              <span className="signup-admin-step-nav__num">{item.key + 1}</span>
              <span className="signup-admin-step-nav__label signup-admin-step-nav__label--full">{item.full}</span>
              <span className="signup-admin-step-nav__label signup-admin-step-nav__label--short">{item.short}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function SignupAdmin() {
  const screens = useBreakpoint();
  const formSize = screens.md ? 'middle' : 'small';

  const [params] = useSearchParams();
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const phonePrefill = useMemo(() => (params.get('phone') || ''), [params]);

  const normalizePhone = (p) => String(p || '').replace(/[^0-9]/g, '').slice(-10);

  const onFinish = async (values) => {
    const phone = normalizePhone(values.phone);
    const name = String(values.name || '').trim();
    const businessName = String(values.businessName || '').trim();
    const password = String(values.password || '').trim();
    if (password.length < 8) {
      message.error('Password must be at least 8 characters');
      return;
    }
    const businessEmail = String(values.businessEmail || '').trim();
    const state = String(values.state || '').trim();
    const city = String(values.city || '').trim();
    const channelPartnerId = String(values.channelPartnerId || '').trim();
    const roleDescription = String(values.roleDescription || '').trim();
    const employeeCount = String(values.employeeCount || '').trim();
    const contactPersonName = String(values.contactPersonName || '').trim();
    const address = String(values.address || '').trim();
    const birthDate = values.birthDate ? values.birthDate.format('YYYY-MM-DD') : null;
    const anniversaryDate = values.anniversaryDate ? values.anniversaryDate.format('YYYY-MM-DD') : null;
    const gstNumber = String(values.gstNumber || '').trim();

    if (!phone || phone.length !== 10) {
      message.error('Enter valid 10-digit phone');
      return;
    }
    if (!name || !businessName) {
      message.error('Name and Business Name are required');
      return;
    }

    try {
      setLoading(true);
      const res = await api.post('/auth/signup-admin', {
        phone,
        name,
        businessName,
        password,
        businessEmail,
        state,
        city,
        channelPartnerId,
        roleDescription,
        employeeCount,
        contactPersonName,
        address,
        birthDate,
        anniversaryDate,
        gstNumber,
      });
      if (res.data?.success) {
        setSubmitted(true);
      } else {
        message.error(res.data?.message || 'Signup failed');
      }
    } catch {
      message.error('Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const goNext = async () => {
    try {
      await form.validateFields(STEP_1_FIELDS);
      setStep(1);
    } catch {
      message.warning('Please complete the required fields on this step');
    }
  };

  return (
    <Layout className="signup-admin-page">
      <Content style={{ padding: 0 }}>
        <Row className="signup-admin-row" wrap={false}>
          <Col xs={0} md={10} lg={11} className="signup-admin-hero">
            <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: 280, height: 280, borderRadius: '50%', background: 'rgba(36,99,235,0.05)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
              <Tag
                color="blue"
                style={{ borderRadius: 20, padding: '0 10px', fontWeight: 600, background: 'rgba(36,99,235,0.1)', color: '#2463EB', border: 'none', marginBottom: 12 }}
              >
                VetanSutra
              </Tag>
              <Title level={1} className="signup-admin-hero__title">
                Advanced Intelligence for <span style={{ color: '#2463EB' }}>Modern Enterprise.</span>
              </Title>
              <img src="/pina.png" alt="Dashboard" className="signup-admin-hero__img" />
            </div>
          </Col>

          <Col xs={24} md={14} lg={13} className="signup-admin-form-col">
            <div className="signup-admin-form-wrap">
              {submitted ? (
                <div className="signup-admin-success">
                  <div style={{ marginBottom: 12, fontSize: 48 }}>✅</div>
                  <Title level={3} style={{ fontWeight: 800, marginBottom: 8, color: '#2564EB' }}>Signup Received</Title>
                  <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 20 }}>
                    Thank you. Our team will respond within 24–48 hours.
                  </Text>
                  <Button type="primary" className="signup-admin-btn-primary" onClick={() => navigate('/')}>
                    Go to Login
                  </Button>
                </div>
              ) : (
                <>
                  <div className="signup-admin-heading">
                    <Title level={2}>Claim your spot</Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Attendance & payroll onboarding
                    </Text>
                  </div>

                  <SignupStepNav step={step} onStepChange={setStep} />

                  <Form
                    form={form}
                    className="signup-admin-form"
                    layout="vertical"
                    size={formSize}
                    onFinish={onFinish}
                    initialValues={{ phone: phonePrefill }}
                    requiredMark={false}
                  >
                    <div style={{ display: step === 0 ? 'block' : 'none' }}>
                      <Row gutter={[8, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item label={label('Your Name')} name="name" rules={[{ required: true, message: 'Required' }]}>
                            <Input className="signup-admin-input" prefix={<UserOutlined style={{ color: '#98a2b3' }} />} placeholder="Full name" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={label('Phone')}
                            name="phone"
                            rules={[{ required: true, message: 'Required' }, { len: 10, message: '10 digits' }]}
                          >
                            <Input className="signup-admin-input" prefix={<PhoneOutlined style={{ color: '#98a2b3' }} />} maxLength={10} placeholder="10-digit" inputMode="numeric" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label={label('Business Name')} name="businessName" rules={[{ required: true, message: 'Required' }]}>
                        <Input className="signup-admin-input" prefix={<ShopOutlined style={{ color: '#98a2b3' }} />} placeholder="Company name" />
                      </Form.Item>

                      <Form.Item
                        label={label('Password')}
                        name="password"
                        rules={[{ required: true, message: 'Required' }, { min: 8, message: 'Min 8 chars' }]}
                      >
                        <Input.Password className="signup-admin-input" prefix={<LockOutlined style={{ color: '#98a2b3' }} />} placeholder="Min. 8 characters" />
                      </Form.Item>

                      <Row gutter={[8, 0]}>
                        <Col xs={24} sm={14}>
                          <Form.Item label={label('Business Email')} name="businessEmail">
                            <Input className="signup-admin-input" prefix={<MailOutlined style={{ color: '#98a2b3' }} />} placeholder="email@company.com" type="email" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={10}>
                          <Form.Item label={label('GST')} name="gstNumber">
                            <Input className="signup-admin-input" prefix={<SafetyCertificateOutlined style={{ color: '#98a2b3' }} />} placeholder="Optional" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label={label('Address')} name="address">
                        <Input.TextArea className="signup-admin-input" rows={2} placeholder="Street, area, pin" />
                      </Form.Item>

                      <Row gutter={[8, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item label={label('State')} name="state">
                            <Input className="signup-admin-input" placeholder="State" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label={label('City')} name="city">
                            <Input className="signup-admin-input" placeholder="City" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item className="signup-admin-actions">
                        <Button type="primary" className="signup-admin-btn-primary" onClick={goNext} icon={<ArrowRightOutlined />}>
                          Continue
                        </Button>
                      </Form.Item>
                    </div>

                    <div style={{ display: step === 1 ? 'block' : 'none' }}>
                      <Form.Item label={label('Contact person')} name="contactPersonName">
                        <Input className="signup-admin-input" prefix={<UserOutlined style={{ color: '#98a2b3' }} />} placeholder="Optional" />
                      </Form.Item>

                      <Row gutter={[8, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item label={label('Employees')} name="employeeCount">
                            <Select placeholder="Select" className="signup-admin-input">
                              <Select.Option value="Less than 20">Less than 20</Select.Option>
                              <Select.Option value="20-100">20–100</Select.Option>
                              <Select.Option value="100-500">100–500</Select.Option>
                              <Select.Option value="More than 500">500+</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label={label('Channel partner ID')} name="channelPartnerId">
                            <Input className="signup-admin-input" prefix={<IdcardOutlined style={{ color: '#98a2b3' }} />} placeholder="Optional" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label={label('Your role')} name="roleDescription">
                        <Input.TextArea className="signup-admin-input" rows={2} placeholder="Owner / HR / Manager" />
                      </Form.Item>

                      <Row gutter={[8, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item label={label('Birth date')} name="birthDate">
                            <DatePicker className="signup-admin-input" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label={label('Anniversary')} name="anniversaryDate">
                            <DatePicker className="signup-admin-input" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={8} className="signup-admin-actions">
                        <Col xs={24} sm={12}>
                          <Button icon={<ArrowLeftOutlined />} className="signup-admin-btn-secondary" onClick={() => setStep(0)}>
                            Back
                          </Button>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Button type="primary" htmlType="submit" loading={loading} icon={<ArrowRightOutlined />} className="signup-admin-btn-primary">
                            Onboard
                          </Button>
                        </Col>
                      </Row>
                    </div>

                    <div className="signup-admin-login-link">
                      <Text type="secondary" style={{ fontSize: 13 }}>Have an account? </Text>
                      <Button type="link" onClick={() => navigate('/')} style={{ padding: 0, height: 'auto', fontWeight: 600 }}>
                        Log in
                      </Button>
                    </div>
                  </Form>
                </>
              )}

              <div className="signup-admin-footer">
                <Text type="secondary">
                  <a href="https://vetansutra.com/policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2463EB' }}>
                    Privacy Policy
                  </a>
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}
